import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLandingMetadata } from '../hooks/useLandingMetadata';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowLeft,
  Sparkles,
  Users,
  Crown,
  CreditCard,
  Lock,
  MonitorSmartphone,
  Flag,
  Handshake,
  Megaphone,
  CheckCheck,
  Send,
  ShieldCheck,
  Clock,
  Building2,
  Languages,
  ChevronRight,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import LanguageSwitcher from '../components/LanguageSwitcher';
import LegalFooter from '../components/LegalFooter';
import { useAuth } from '../context/AuthContext';
import HelpFormModal from '../components/help/HelpFormModal';

/**
 * Phase 41 · Contact page.
 *
 * Public page (also accessible from AppShell for authenticated visitors).
 * Follows the psychology-first sequence defined in
 * `/app/memory/landing_framework.md` adapted to a support/contact context:
 *
 *   1. Hero — welcome, empathy
 *   2. How can we help — themed cards
 *   3. Direct channels — dedicated emails with copy + write actions
 *   4. Form — categorised submission
 *   5. Before contacting us — FAQ shortcut
 *   6. Response times — honest, no over-promising
 *   7. Company info — Bull Marketing S.r.l.s.
 *   8. Security reminder — anti-phishing
 *   9. Languages
 *  10. Final CTA — scroll back to form
 *
 * Design:
 *   - Dark theme, gold accents (matches Nymia global palette)
 *   - Grid of soft-glass cards; ample whitespace; no stock photos
 *   - Framer Motion for very light entrance animations (respects
 *     prefers-reduced-motion)
 *   - Mobile-first responsive layout
 */

const HELP_AREAS = [
  { key: 'users', icon: Users },
  { key: 'creators', icon: Crown },
  { key: 'payments', icon: CreditCard },
  { key: 'privacy', icon: Lock },
  { key: 'technical', icon: MonitorSmartphone },
  { key: 'reports', icon: Flag },
  { key: 'partnership', icon: Handshake },
  { key: 'press', icon: Megaphone },
];

const CATEGORIES = [
  'support', 'creator', 'payments', 'wallet', 'lives',
  'one_to_one', 'privacy', 'technical', 'reports', 'partnership', 'other',
];

// Simple email regex — enough for client-side validation, backend re-validates.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Small, self-contained card wrapper with hover halo. */
const GlassCard = ({ children, className = '', testId }) => (
  <div
    data-testid={testId}
    className={`group rounded-2xl border border-white/5 bg-white/[0.02] p-6 backdrop-blur transition-colors hover:border-amber-500/25 hover:bg-white/[0.035] ${className}`}
  >
    {children}
  </div>
);

/** Vertical section separator with soft label. */
const SectionHeading = ({ eyebrow, title, subtitle, testId }) => (
  <div className="mx-auto max-w-3xl text-center" data-testid={testId}>
    {eyebrow && (
      <p className="mb-3 text-[11px] uppercase tracking-[0.28em] text-amber-200/70">
        {eyebrow}
      </p>
    )}
    <h2 className="text-2xl font-semibold text-foreground md:text-3xl">{title}</h2>
    {subtitle && (
      <p className="mt-3 text-sm text-muted-foreground md:text-[15px]">{subtitle}</p>
    )}
  </div>
);

const ContactPage = () => {
  useLandingMetadata('contact');
  const { t, i18n } = useTranslation('contact');
  const navigate = useNavigate();
  const { user } = useAuth();
  const reduceMotion = useReducedMotion();
  const isEmbedded = !!user;

  // ------------------------- Form state --------------------------
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: '',
    subject: '',
    message: '',
    website: '', // honeypot — MUST stay empty
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState({ status: 'idle', reference: '', errorMessage: '' });
  // Phase 48 · Help Center dynamic form modal. Clicking a Help Area
  // card opens the modal with that form's key. `null` = closed.
  const [activeFormKey, setActiveFormKey] = useState(null);

  const validate = () => {
    const next = {};
    if (!formData.name || formData.name.trim().length < 2) next.name = t('form.errors.name_required');
    if (!formData.email || !EMAIL_RE.test(formData.email)) next.email = t('form.errors.email_invalid');
    if (!formData.category) next.category = t('form.errors.category_required');
    if (!formData.subject || formData.subject.trim().length < 3) next.subject = t('form.errors.subject_required');
    if (!formData.message || formData.message.trim().length < 20) next.message = t('form.errors.message_short');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleChange = (field) => (event) => {
    const value = event?.target ? event.target.value : event;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setSubmitState({ status: 'idle', reference: '', errorMessage: '' });
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, locale: i18n.language || 'it' }),
      });
      if (res.status === 429) {
        setSubmitState({ status: 'error', reference: '', errorMessage: t('form.errors.rate_limited') });
        return;
      }
      if (!res.ok) {
        setSubmitState({ status: 'error', reference: '', errorMessage: t('form.errors.generic') });
        return;
      }
      const json = await res.json();
      setSubmitState({ status: 'success', reference: json.reference || '', errorMessage: '' });
      setFormData({ name: '', email: '', category: '', subject: '', message: '', website: '' });
    } catch (e) {
      setSubmitState({ status: 'error', reference: '', errorMessage: t('form.errors.generic') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fadeUp = reduceMotion
    ? { initial: {}, whileInView: {}, transition: {} }
    : {
        initial: { opacity: 0, y: 16 },
        whileInView: { opacity: 1, y: 0 },
        transition: { duration: 0.5, ease: 'easeOut' },
        viewport: { once: true, margin: '-40px' },
      };

  const helpAreas = useMemo(() => HELP_AREAS, []);

  // Scroll helper for CTA final button
  const scrollToForm = () => {
    const el = document.getElementById('contact-form');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const pageContent = (
    <main className="mx-auto w-full max-w-6xl px-4 py-10 md:py-16" data-testid="contact-page">

      {/* 1 · HERO -------------------------------------------------- */}
      <motion.section {...fadeUp} className="mb-16 md:mb-24 text-center" data-testid="contact-hero">
        <p className="mb-4 text-[11px] uppercase tracking-[0.32em] text-amber-200/80" data-testid="contact-eyebrow">
          {t('hero.eyebrow')}
        </p>
        <h1 className="text-4xl font-semibold leading-tight text-foreground md:text-6xl">
          {t('hero.title')}
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
          {t('hero.subtitle')}
        </p>
      </motion.section>

      {/* 2 · HELP AREAS ------------------------------------------- */}
      <motion.section {...fadeUp} className="mb-20" data-testid="contact-help-areas">
        <SectionHeading
          title={t('help_areas.title')}
          subtitle={t('help_areas.description')}
          testId="contact-help-heading"
        />
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {helpAreas.map(({ key, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveFormKey(key)}
              className="group rounded-2xl border border-white/5 bg-white/[0.02] p-6 text-left backdrop-blur transition-all hover:-translate-y-0.5 hover:border-amber-500/40 hover:bg-white/[0.045] hover:shadow-lg hover:shadow-amber-500/5 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
              data-testid={`contact-help-card-${key}`}
              aria-label={t(`help_areas.cards.${key}.title`)}
            >
              <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/10 text-amber-200 transition-colors group-hover:bg-amber-500/20">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="text-base font-semibold text-foreground">
                {t(`help_areas.cards.${key}.title`)}
              </h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                {t(`help_areas.cards.${key}.description`)}
              </p>
              <span className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold gold-text opacity-0 transition-opacity group-hover:opacity-100">
                {t('help_areas.open_form')}
                <ChevronRight className="h-3 w-3" />
              </span>
            </button>
          ))}
        </div>
      </motion.section>

      {/* 4 · FORM ------------------------------------------------- */}
      <motion.section {...fadeUp} className="mb-20" id="contact-form" data-testid="contact-form">
        <SectionHeading
          title={t('form.title')}
          subtitle={t('form.description')}
        />
        <div className="mx-auto mt-10 max-w-3xl rounded-3xl border border-white/5 bg-white/[0.025] p-6 backdrop-blur md:p-10">
          {submitState.status === 'success' ? (
            <div className="text-center" data-testid="contact-form-success">
              <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/15 text-amber-200">
                <CheckCheck className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">{t('form.success.title')}</h3>
              <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-muted-foreground">
                {t('form.success.body', { reference: submitState.reference })}
              </p>
              <Button
                variant="outline"
                className="mt-6"
                onClick={() => setSubmitState({ status: 'idle', reference: '', errorMessage: '' })}
                data-testid="contact-form-reset"
              >
                {t('form.success.cta')}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              {/* Honeypot — hidden from users, visible to bots */}
              <div className="hidden" aria-hidden="true">
                <label>
                  Website
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={handleChange('website')}
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <Label htmlFor="contact-name" className="text-[13px]">
                    {t('form.fields.name.label')}
                  </Label>
                  <Input
                    id="contact-name"
                    value={formData.name}
                    onChange={handleChange('name')}
                    placeholder={t('form.fields.name.placeholder')}
                    autoComplete="name"
                    className="mt-1.5"
                    data-testid="contact-input-name"
                  />
                  {errors.name && (
                    <p className="mt-1 flex items-center gap-1 text-[12px] text-red-400" data-testid="contact-error-name">
                      <AlertCircle className="h-3 w-3" /> {errors.name}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="contact-email" className="text-[13px]">
                    {t('form.fields.email.label')}
                  </Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange('email')}
                    placeholder={t('form.fields.email.placeholder')}
                    autoComplete="email"
                    className="mt-1.5"
                    data-testid="contact-input-email"
                  />
                  {errors.email && (
                    <p className="mt-1 flex items-center gap-1 text-[12px] text-red-400" data-testid="contact-error-email">
                      <AlertCircle className="h-3 w-3" /> {errors.email}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-[13px]">{t('form.fields.category.label')}</Label>
                <Select
                  value={formData.category}
                  onValueChange={handleChange('category')}
                >
                  <SelectTrigger className="mt-1.5" data-testid="contact-input-category">
                    <SelectValue placeholder={t('form.fields.category.placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`contact-category-option-${c}`}>
                        {t(`form.fields.category.options.${c}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.category && (
                  <p className="mt-1 flex items-center gap-1 text-[12px] text-red-400" data-testid="contact-error-category">
                    <AlertCircle className="h-3 w-3" /> {errors.category}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="contact-subject" className="text-[13px]">
                  {t('form.fields.subject.label')}
                </Label>
                <Input
                  id="contact-subject"
                  value={formData.subject}
                  onChange={handleChange('subject')}
                  placeholder={t('form.fields.subject.placeholder')}
                  className="mt-1.5"
                  data-testid="contact-input-subject"
                />
                {errors.subject && (
                  <p className="mt-1 flex items-center gap-1 text-[12px] text-red-400" data-testid="contact-error-subject">
                    <AlertCircle className="h-3 w-3" /> {errors.subject}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="contact-message" className="text-[13px]">
                  {t('form.fields.message.label')}
                </Label>
                <Textarea
                  id="contact-message"
                  value={formData.message}
                  onChange={handleChange('message')}
                  placeholder={t('form.fields.message.placeholder')}
                  rows={6}
                  className="mt-1.5 resize-none"
                  data-testid="contact-input-message"
                />
                {errors.message && (
                  <p className="mt-1 flex items-center gap-1 text-[12px] text-red-400" data-testid="contact-error-message">
                    <AlertCircle className="h-3 w-3" /> {errors.message}
                  </p>
                )}
              </div>

              {submitState.status === 'error' && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-[13px] text-red-200" data-testid="contact-error-generic">
                  {submitState.errorMessage}
                </div>
              )}

              <p className="text-[11.5px] leading-relaxed text-muted-foreground/90">
                {t('form.privacy_note')}
              </p>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="gold-gradient w-full text-black md:w-auto md:min-w-[220px]"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('form.sending')}</>
                ) : (
                  <><Send className="mr-2 h-4 w-4" /> {t('form.submit')}</>
                )}
              </Button>
            </form>
          )}
        </div>
      </motion.section>

      {/* 5 · BEFORE CONTACT (FAQ shortcut) ------------------------ */}
      <motion.section {...fadeUp} className="mb-20" data-testid="contact-before">
        <div className="rounded-3xl border border-amber-500/15 bg-amber-500/[0.04] p-8 text-center md:p-12">
          <p className="mb-2 text-[11px] uppercase tracking-[0.28em] text-amber-200/70">
            {t('meta.brand')}
          </p>
          <h2 className="text-2xl font-semibold text-foreground md:text-3xl">
            {t('before_contact.title')}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-[15px]">
            {t('before_contact.description')}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/faq/users')}
              data-testid="contact-cta-faq-users"
            >
              {t('before_contact.cta_users')}
              <ChevronRight className="ml-1.5 h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/faq/creators')}
              data-testid="contact-cta-faq-creators"
            >
              {t('before_contact.cta_creators')}
              <ChevronRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      </motion.section>

      {/* 6 · RESPONSE TIMES --------------------------------------- */}
      <motion.section {...fadeUp} className="mb-20" data-testid="contact-response-times">
        <SectionHeading
          title={t('response_times.title')}
          subtitle={t('response_times.description')}
        />
        <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
          {['general', 'urgent', 'privacy'].map((key) => (
            <GlassCard key={key} testId={`contact-time-${key}`}>
              <div className="mb-4 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/10 text-amber-200">
                <Clock className="h-4 w-4" />
              </div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                {t(`response_times.items.${key}.label`)}
              </p>
              <p className="mt-1 text-sm font-medium text-foreground">
                {t(`response_times.items.${key}.value`)}
              </p>
            </GlassCard>
          ))}
        </div>
        <p className="mx-auto mt-6 max-w-3xl text-center text-[13px] leading-relaxed text-muted-foreground">
          {t('response_times.footnote')}
        </p>
      </motion.section>

      {/* 7 · COMPANY INFO ----------------------------------------- */}
      <motion.section {...fadeUp} className="mb-20" data-testid="contact-company">
        <div className="mx-auto max-w-4xl rounded-3xl border border-white/5 bg-white/[0.02] p-8 md:p-12">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-[auto_1fr] md:items-start">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-amber-500/10 text-amber-200">
              <Building2 className="h-6 w-6" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.28em] text-amber-200/70">
                {t('company.title')}
              </p>
              <h3 className="mt-2 text-xl font-semibold text-foreground">{t('company.name')}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {t('company.description')}
              </p>
              <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t('meta.brand')}</p>
                  <address className="mt-1 space-y-0.5 not-italic text-sm text-foreground">
                    {t('company.address_lines', { returnObjects: true }).map((line, idx) => (
                      <div key={idx}>{line}</div>
                    ))}
                  </address>
                </div>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{t('company.vat_label')}</span>
                    <div className="font-mono text-foreground">{t('company.vat_value')}</div>
                  </div>
                  <div>
                    <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{t('company.rea_label')}</span>
                    <div className="font-mono text-foreground">{t('company.rea_value')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* 8 · SECURITY REMINDER ------------------------------------ */}
      <motion.section {...fadeUp} className="mb-20" data-testid="contact-security">
        <div className="mx-auto max-w-4xl rounded-3xl border border-emerald-500/20 bg-emerald-500/[0.04] p-8 md:p-12">
          <div className="mb-5 flex items-center gap-3">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-200">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <h3 className="text-lg font-semibold text-foreground md:text-xl">
              {t('security.title')}
            </h3>
          </div>
          <p className="text-sm leading-relaxed text-muted-foreground">{t('security.intro')}</p>
          <ul className="mt-4 space-y-2 text-sm text-foreground/90">
            {t('security.items', { returnObjects: true }).map((item, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
            {t('security.footer')}
          </p>
        </div>
      </motion.section>

      {/* 9 · LANGUAGES -------------------------------------------- */}
      <motion.section {...fadeUp} className="mb-20" data-testid="contact-languages">
        <div className="mx-auto flex max-w-2xl flex-col items-center gap-4 rounded-3xl border border-white/5 bg-white/[0.02] p-8 text-center md:p-10">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/10 text-amber-200">
            <Languages className="h-5 w-5" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">{t('languages.title')}</h3>
          <p className="max-w-md text-sm text-muted-foreground">{t('languages.description')}</p>
          <div className="flex gap-2 text-xs">
            <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 font-medium text-foreground">
              {t('languages.it')}
            </span>
            <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 font-medium text-foreground">
              {t('languages.en')}
            </span>
          </div>
        </div>
      </motion.section>

      {/* 10 · FINAL CTA ------------------------------------------- */}
      <motion.section {...fadeUp} data-testid="contact-final-cta">
        <div className="mx-auto max-w-3xl rounded-3xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.06] to-transparent p-10 text-center md:p-14">
          <h3 className="text-2xl font-semibold text-foreground md:text-3xl">
            {t('final_cta.title')}
          </h3>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground md:text-[15px]">
            {t('final_cta.subtitle')}
          </p>
          <Button
            onClick={scrollToForm}
            className="gold-gradient mt-7 text-black"
            size="lg"
            data-testid="contact-final-cta-btn"
          >
            <Send className="mr-2 h-4 w-4" /> {t('final_cta.button')}
          </Button>
        </div>
      </motion.section>

      {/* Phase 48 · Help Center dynamic form modal — mounted once,
          key toggle controls visibility. */}
      <HelpFormModal
        formKey={activeFormKey}
        open={!!activeFormKey}
        onOpenChange={(o) => setActiveFormKey(o ? activeFormKey : null)}
      />

      {/* Public footer only on standalone route */}
      {!isEmbedded && (
        <div className="mt-16 border-t border-white/5 pt-8">
          <LegalFooter />
        </div>
      )}
    </main>
  );

  // Standalone layout — anonymous visitor: brand header + back link
  if (!isEmbedded) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <div className="border-b border-border">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold" data-testid="contact-brand-link">
              <Sparkles className="h-4 w-4 gold-text" />
              <span className="text-lg gold-text">{t('meta.brand', { defaultValue: 'Nymia' })}</span>
            </Link>
            <div className="flex items-center gap-4">
              <LanguageSwitcher variant="compact" testId="contact-language-switcher" />
              <Link to="/" className="inline-flex items-center gap-1 text-xs text-muted-foreground transition hover:text-amber-200" data-testid="contact-back-home">
                <ArrowLeft className="h-3 w-3" /> {t('meta.back_home')}
              </Link>
            </div>
          </div>
        </div>
        {pageContent}
      </div>
    );
  }

  // Embedded — logged-in user, AppShell provides chrome
  return pageContent;
};

export default ContactPage;
